import React, { useState, useEffect, useRef } from "react";
import styles from "./ImageViewer.module.css";

import Draggable from "react-draggable";

// Custom components
import { SquareIconUtilityButton } from "../Buttons/Buttons";
import { IconValueSlider } from "../Sliders/Sliders";

// Icons
import { ArrowPathIcon } from "@heroicons/react/24/outline";

import { getImageSrc, handleImageLoad } from "../../helpers/imageLoadUtils";

function ImageViewer(props) {
  const imageMinZoom = 10;
  const imageMaxZoom = 1000;
  const zoomDampener = 3000;

  const [imageSrc, setImageSrc] = useState("");
  useEffect(() => {
    setImageSrc(getImageSrc(props.modalImage));
  }, []);

  const [imageLoading, setImageLoading] = useState(true);

  const updatePosition = (data) => {
    setPosition({ x: data.x, y: data.y });
  };

  // Handle image scroll zooming
  const handleScrollZoom = (event) => {
    if (imageZoom < imageMaxZoom && imageZoom > imageMinZoom) {
      if (
        imageZoom - event.deltaY * (imageZoom / zoomDampener) < imageMaxZoom &&
        imageZoom - event.deltaY * (imageZoom / zoomDampener) > imageMinZoom
      ) {
        setImageZoom(imageZoom - event.deltaY * (imageZoom / zoomDampener));
      }
    } else if (imageZoom <= imageMinZoom) {
      setImageZoom(imageMinZoom);
    } else if (imageZoom >= imageMaxZoom) {
      setImageZoom(imageMaxZoom);
    }
  };

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [imageZoom, setImageZoom] = useState(100);
  // const [drag, setDrag] = useState(false);

  // Touch Controls START
  const [rotation, setRotation] = useState(0);

  const initialDistance = useRef(0);
  const initialAngle = useRef(0);

  const handleTouchStart = (event) => {
    if (event.touches.length === 2) {
      initialDistance.current = getDistance(event.touches[0], event.touches[1]);
      initialAngle.current = getAngle(event.touches[0], event.touches[1]);
    }
  };

  const handleTouchMove = (event) => {
    if (event.touches.length === 2) {
      const currentDistance = getDistance(event.touches[0], event.touches[1]);
      const currentAngle = getAngle(event.touches[0], event.touches[1]);

      // Calculate and set the scale (zoom)
      // Keep image zoom within bounds
      const scaleChange = currentDistance / initialDistance.current;
      setImageZoom((prevScale) => {
        const newScale = prevScale * scaleChange;
        if (newScale >= 10 && newScale <= 1000) {
          return newScale;
        } else if (newScale < 10) {
          return 10;
        } else if (newScale > 1000) {
          return 1000;
        }
      });

      // Calculate and set the rotation
      const angleDifference = currentAngle - initialAngle.current;
      setRotation((prevRotation) => prevRotation + angleDifference);

      // Update initial values for the next move
      initialDistance.current = currentDistance;
      initialAngle.current = currentAngle;
    }
  };

  const getDistance = (touch1, touch2) => {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const getAngle = (touch1, touch2) => {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return (Math.atan2(dy, dx) * 180) / Math.PI;
  };
  // Touch Controls END

  return (
    <div className="flex flex-col h-full w-full Xbg-blue-500">
      <div className="flex flex-row items-center justify-center w-full py-2">
        <SquareIconUtilityButton
          Icon={ArrowPathIcon}
          iconSize="h-6 w-6"
          actionFunction={() => {
            setPosition({ x: 0, y: 0 });
            setImageZoom(100);
            setRotation(0);
          }}
        />

        <div className="px-2" />

        <IconValueSlider
          sliderValue={imageZoom}
          sliderMin={imageMinZoom}
          sliderMax={imageMaxZoom}
          sliderStep={0.1}
          onChangeFunction={(event) => {
            setImageZoom(event.target.value);
          }}
          showIcon={false}
          showValue={false}
        />
      </div>

      <div
        className="flex flex-col items-center justify-center w-full h-full
                   overflow-hidden rounded-2xl bg-neutral-200"
        onWheel={(event) => {
          handleScrollZoom(event);
        }}
      >
        <Draggable
          onDrag={(e, data) => {
            // setDrag(true);
            updatePosition(data);
          }}
          onStop={() => {
            // setDrag(false);
          }}
          position={{ x: position.x, y: position.y }}
        >
          <div className="flex items-center justify-center w-full h-full select-none cursor-grab Xbg-green-500">
            {imageLoading && <div className={styles["loader"]} />}
            <img
              src={imageSrc}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              alt={"Detailed view"}
              onLoad={(e) =>
                handleImageLoad({
                  imageElement: e.target,
                  setItemLoading: setImageLoading(false),
                })
              }
              style={
                !imageLoading
                  ? {
                      height: imageZoom + "%",
                      rotate: rotation + "deg",
                      width: "auto",
                      maxHeight: "2000%",
                      maxWidth: "2000%",
                    }
                  : {
                      position: "absolute",
                      opacity: "0",
                    }
              }
              className="touch-none"
              draggable="false"
            />
          </div>
        </Draggable>
      </div>
    </div>
  );
}

export default ImageViewer;
